/**
 * HUD \u4e0e\u7ed3\u7b97\u5f39\u7a97 \u2014\u2014 \u53ea\u8bfb Game \u5feb\u7167\uff0c\u4e0d\u6301\u6709\u4e1a\u52a1\u72b6\u6001\u3002
 */

import type { GameSnapshot } from '../core/game';
import { getGemSpec } from '../render/palette';

export interface HudHandles {
  level: HTMLElement;
  moves: HTMLElement;
  score: HTMLElement;
  goals: HTMLElement;
  stars: HTMLElement;
  toast: HTMLElement;
  modal: HTMLElement;
  modalTitle: HTMLElement;
  modalStars: HTMLElement;
  modalBody: HTMLElement;
  modalRetry: HTMLButtonElement;
  modalNext: HTMLButtonElement;
  btnRestart: HTMLButtonElement;
}

export function grabHud(): HudHandles {
  const q = <T extends HTMLElement>(id: string): T => {
    const el = document.getElementById(id);
    if (!el) throw new Error(`\u7f3a\u5c11 #${id}`);
    return el as T;
  };
  return {
    level: q('hud-level'),
    moves: q('hud-moves'),
    score: q('hud-score'),
    goals: q('hud-goals'),
    stars: q('hud-stars'),
    toast: q('toast'),
    modal: q('modal'),
    modalTitle: q('modal-title'),
    modalStars: q('modal-stars'),
    modalBody: q('modal-body'),
    modalRetry: q<HTMLButtonElement>('modal-retry'),
    modalNext: q<HTMLButtonElement>('modal-next'),
    btnRestart: q<HTMLButtonElement>('btn-restart'),
  };
}

export function renderHud(h: HudHandles, game: GameSnapshot): void {
  h.level.textContent = String(game.level.levelId);
  h.moves.textContent = String(game.movesLeft);
  h.moves.classList.toggle('low', game.movesLeft <= 3);
  h.score.textContent = game.score.toLocaleString('zh-CN');

  // \u76ee\u6807
  h.goals.innerHTML = '';
  for (const g of game.level.goals) {
    const cur = game.goalProgress.get(g.color) ?? 0;
    const done = cur >= g.count;
    const el = document.createElement('div');
    el.className = 'goal' + (done ? ' done' : '');
    const spec = getGemSpec(g.color);
    el.innerHTML = `
      <span class="goal-dot" style="background: radial-gradient(circle at 34% 30%, ${spec.light}, ${spec.mid} 55%, ${spec.dark})"></span>
      <span class="goal-count"><b>${Math.min(cur, g.count)}</b>/${g.count}</span>
    `;
    h.goals.appendChild(el);
  }

  // \u661f\u7ebf\u9884\u89c8
  h.stars.innerHTML = '';
  const preview = previewStars(game);
  for (let i = 1; i <= 3; i++) {
    const s = document.createElement('div');
    s.className = 'star' + (i <= preview ? ' on' : '');
    h.stars.appendChild(s);
  }
}

/** \u8fdb\u884c\u4e2d\u9884\u89c8\u661f\u7ea7\uff08\u7ed3\u7b97\u540e\u7528\u771f\u5b9e\u503c\uff09 */
function previewStars(game: GameSnapshot): number {
  if (game.finished !== 'playing') return game.stars;
  const { starLine2, starLine3 } = game.level;
  if (game.score >= starLine3) return 3;
  if (game.score >= starLine2) return 2;
  return 1;
}

let toastTimer: number | undefined;
export function showToast(h: HudHandles, msg: string, ms = 1200): void {
  h.toast.textContent = msg;
  h.toast.classList.add('show');
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => h.toast.classList.remove('show'), ms);
}

export function showResultModal(h: HudHandles, game: GameSnapshot): void {
  const won = game.finished === 'won';
  h.modalTitle.textContent = won ? '\u8fc7\u5173' : '\u6b65\u6570\u7528\u5b8c';
  h.modalTitle.className = 'modal-title ' + (won ? 'win' : 'lose');

  h.modalStars.innerHTML = '';
  if (won) {
    for (let i = 1; i <= 3; i++) {
      const s = document.createElement('div');
      s.className = 'star' + (i <= game.stars ? ' on' : '');
      h.modalStars.appendChild(s);
    }
  }

  const remain = game.movesLeft;
  h.modalBody.innerHTML = won
    ? `\u5f97\u5206 <b>${game.score.toLocaleString('zh-CN')}</b><br/>\u5269\u4f59\u6b65\u6570 <b>${remain}</b>\uff08\u5df2\u8ba1\u5165\u5956\u52b1\u5206\uff09`
    : `\u5f97\u5206 <b>${game.score.toLocaleString('zh-CN')}</b><br/>\u79bb\u76ee\u6807\u8fd8\u5dee\u4e00\u70b9\uff0c\u518d\u8bd5\u4e00\u6b21`;

  h.modalNext.style.display = won ? '' : 'none';
  h.modal.classList.remove('hidden');
}

export function hideResultModal(h: HudHandles): void {
  h.modal.classList.add('hidden');
}
